import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Check, Copy, Link2, Loader2, X } from 'lucide-react';

/**
 * The link for a shared chat, and the switch that turns it off.
 *
 * Says plainly what a recipient will and will not see: the result tables are
 * withheld server-side, because those rows were selected under the sharer's own
 * filters. Someone deciding whether to send a link needs that on screen, not in
 * a doc.
 *
 * Nothing is granted until asked for. Opening the dialog used to mint a link,
 * and every change of reach minted another — each one a token issued, an old one
 * revoked, and a row written, for a dialog someone might close without sending
 * anything. The reach is now a choice held here, and one press turns it into one
 * request.
 */

const TITLE_ID = 'share-session-title';
const COPIED_VISIBLE_MS = 2000;

/**
 * How much of each turn a link carries. Cumulative, and these strings are the
 * wire values the backend stores on the grant — it narrows anything it does not
 * recognise, so a typo here shares less rather than more.
 */
export const SCOPES = [
  {
    value: 'messages',
    label: 'Messages only',
    detail: 'The questions and the written answers.',
  },
  {
    value: 'messages_activity',
    label: 'Messages and the activity timeline',
    detail: 'Adds the steps each answer went through, and which tools ran.',
  },
  {
    value: 'messages_activity_queries',
    label: 'Messages, timeline and queries',
    detail: 'Adds the SQL behind each answer — the tables, joins and filters it ran against.',
  },
];
const DEFAULT_SCOPE = SCOPES[0].value;

export default function ShareSessionModal({ open, state, onClose, onRevoke, onCreate }) {
  const [copied, setCopied] = useState(false);
  // The reach and the scope being CHOSEN, which are not yet what was granted. They
  // differ exactly while a change is pending, and that gap is what the footer
  // button and the note under the link are about.
  const [reach, setReach] = useState(!!state.isPublic);
  const [scope, setScope] = useState(state.scope || DEFAULT_SCOPE);
  const copiedTimer = useRef(null);
  const dialogRef = useRef(null);

  useEffect(() => () => clearTimeout(copiedTimer.current), []);

  // Re-seeded from what was actually granted — on open, and after each grant, so
  // a backend that narrows the choice (it treats anything but a literal true as
  // signed-in-only, and any unrecognised scope as the narrowest) leaves the radios
  // telling the truth about the link rather than about the request.
  useEffect(() => { setReach(!!state.isPublic); }, [open, state.isPublic]);
  useEffect(() => { setScope(state.scope || DEFAULT_SCOPE); }, [open, state.scope]);

  useEffect(() => {
    if (!open) {
      setCopied(false);
      return undefined;
    }
    dialogRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const url = state.token ? `${window.location.origin}/s/${state.token}` : '';
  const granted = state.status === 'ready';
  // A link exists and what is on screen is not what it carries.
  const pending = granted && (reach !== !!state.isPublic || scope !== (state.scope || DEFAULT_SCOPE));
  const grantedScope = SCOPES.find((s) => s.value === (state.scope || DEFAULT_SCOPE)) || SCOPES[0];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      clearTimeout(copiedTimer.current);
      setCopied(true);
      copiedTimer.current = setTimeout(() => setCopied(false), COPIED_VISIBLE_MS);
    } catch {
      // Clipboard refused (an insecure origin, a denied permission). The field is
      // selectable, so saying nothing here leaves the reader a working path.
    }
  };

  return createPortal(
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        tabIndex={-1}
        className="animate-rise w-full max-w-lg overflow-hidden rounded-2xl border border-line bg-surface"
        style={{ boxShadow: 'var(--shadow-float)' }}
      >
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <h2 id={TITLE_ID} className="text-lg font-semibold text-fg">Share this chat</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close share dialog"
            className="grid h-8 w-8 place-items-center rounded-lg text-fg-muted transition-colors hover:bg-surface-raised hover:text-fg"
          >
            <X aria-hidden="true" size={18} />
          </button>
        </div>

        <div className="px-6 py-5">
          {state.status === 'loading' && (
            <p className="flex items-center gap-2 text-sm text-fg-secondary">
              <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />
              Creating the link…
            </p>
          )}

          {state.status === 'error' && (
            <p className="text-sm text-danger">{state.error || 'The link could not be created. Try again.'}</p>
          )}

          {granted && (
            <>
              <label htmlFor="share-link" className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
                Link
              </label>
              <div className="mt-1.5 flex gap-2">
                <input
                  id="share-link"
                  readOnly
                  value={url}
                  onFocus={(e) => e.target.select()}
                  className="min-w-0 flex-1 rounded-lg border border-line bg-surface-raised px-3 py-2 font-mono text-xs text-fg outline-none"
                />
                <button
                  type="button"
                  onClick={copy}
                  className="flex shrink-0 items-center gap-1.5 rounded-lg bg-brand px-3 py-2 text-sm font-medium text-brand-fg transition-opacity hover:opacity-90"
                >
                  {copied ? <Check aria-hidden="true" className="h-4 w-4" /> : <Copy aria-hidden="true" className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </>
          )}

          {/* Reach is a choice, and the consequences differ enough that the
              options say them rather than being named "private"/"public". It is
              offered before there is a link, because choosing it is what the
              first press acts on. */}
          {onCreate && state.status !== 'loading' && (
            <fieldset className={granted ? 'mt-4' : ''}>
              <legend className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
                Who can open it
              </legend>
              <div className="mt-2 space-y-2">
                <label className="flex cursor-pointer items-start gap-2 text-sm text-fg">
                  <input
                    type="radio"
                    name="share-reach"
                    checked={!reach}
                    onChange={() => setReach(false)}
                    className="mt-1 accent-[var(--brand)]"
                  />
                  <span>
                    People signed in to {window.location.host}
                    <span className="block text-xs text-fg-muted">Expires in 30 days.</span>
                  </span>
                </label>
                <label className="flex cursor-pointer items-start gap-2 text-sm text-fg">
                  <input
                    type="radio"
                    name="share-reach"
                    checked={reach}
                    onChange={() => setReach(true)}
                    className="mt-1 accent-[var(--brand)]"
                  />
                  <span>
                    Anyone with the link, no sign-in
                    <span className="block text-xs text-fg-muted">
                      Expires in 7 days. Anyone the link reaches can read it, so treat it as public.
                    </span>
                  </span>
                </label>
              </div>
            </fieldset>
          )}

          {/* How much of each turn goes with the link. The reasoning behind
              offering this at all: the timeline names every tool a turn ran and
              the queries spell out the tables, joins and filters the sharer's
              data is organised by, and a reader sent a conclusion does not
              automatically need either. Least by default; widening is a choice. */}
          {onCreate && state.status !== 'loading' && (
            <fieldset className="mt-4">
              <legend className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
                What they see
              </legend>
              <div className="mt-2 space-y-2">
                {SCOPES.map((option) => (
                  <label key={option.value} className="flex cursor-pointer items-start gap-2 text-sm text-fg">
                    <input
                      type="radio"
                      name="share-scope"
                      checked={scope === option.value}
                      onChange={() => setScope(option.value)}
                      className="mt-1 accent-[var(--brand)]"
                    />
                    <span>
                      {option.label}
                      <span className="block text-xs text-fg-muted">{option.detail}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          {/* The facts that decide whether sending this is appropriate. Once a
              link exists they describe THAT link, not the radios above them — a
              chosen-but-not-granted reach would otherwise read as the reach of
              the URL sitting on screen, ready to be sent. */}
          {state.status !== 'loading' && (
            <ul className="mt-4 space-y-1.5 text-sm text-fg-secondary">
              <li className="flex gap-2">
                <Link2 aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-fg-muted" />
                {(granted ? state.isPublic : reach)
                  ? 'Anyone with this link can open it, signed in or not.'
                  : `Anyone signed in to ${window.location.host} can open it.`}
              </li>
              <li className="flex gap-2">
                <span aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-center text-fg-muted">·</span>
                {granted ? grantedScope.label : (SCOPES.find((s) => s.value === scope) || SCOPES[0]).label}
                {' — never the result tables.'}
              </li>
              {granted && state.expiresAt && (
                <li className="flex gap-2">
                  <span aria-hidden="true" className="mt-0.5 h-3.5 w-3.5 shrink-0 text-center text-fg-muted">·</span>
                  The link stops working on {new Date(state.expiresAt).toLocaleDateString()}.
                </li>
              )}
            </ul>
          )}

          {pending && (
            <p className="mt-3 text-sm text-fg-secondary">
              The link above is still the old one. Updating it issues a new link and stops this one working.
            </p>
          )}
        </div>

        {state.status !== 'loading' && (
          <div className="flex items-center justify-between border-t border-line px-6 py-3">
            {granted ? (
              <button
                type="button"
                onClick={onRevoke}
                className="text-sm font-medium text-danger transition-opacity hover:opacity-80"
              >
                Turn off this link
              </button>
            ) : (
              <span />
            )}
            {onCreate && (!granted || pending) ? (
              <button
                type="button"
                onClick={() => onCreate(reach, scope)}
                className="rounded-lg bg-brand px-3 py-2 text-sm font-semibold text-brand-fg transition-opacity hover:opacity-90"
              >
                {granted ? 'Update link' : 'Create link'}
              </button>
            ) : (
              <button
                type="button"
                onClick={onClose}
                className="text-sm font-medium text-fg-secondary transition-colors hover:text-fg"
              >
                Done
              </button>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

ShareSessionModal.propTypes = {
  open: PropTypes.bool,
  state: PropTypes.shape({
    // 'choosing' is the state a dialog opens in: a reach picked, nothing granted.
    status: PropTypes.oneOf(['choosing', 'loading', 'ready', 'error']),
    token: PropTypes.string,
    expiresAt: PropTypes.number,
    isPublic: PropTypes.bool,
    // What the GRANT carries, echoed back by the backend — not what was asked for.
    scope: PropTypes.oneOf(SCOPES.map((s) => s.value)),
    error: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  onRevoke: PropTypes.func.isRequired,
  // Grants a link with the given reach and scope. The ONLY thing in this dialog
  // that reaches the network, and only ever from a press.
  onCreate: PropTypes.func,
};

ShareSessionModal.defaultProps = { open: false, state: { status: 'choosing' } };
